import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle2, Send, MessageCircle } from 'lucide-react';
import { useSite } from '../context/SiteContext';
import { pathFor } from '../i18n/routes';
import useSEO from '../hooks/useSEO';
import { MaskedLines, Kicker, Reveal } from '../components/Reveal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  'w-full border-b border-zinc-800 bg-transparent py-3 text-white placeholder-zinc-600 outline-none transition-colors duration-300 focus:border-[var(--brand)]';
const selectCls =
  'w-full appearance-none border-b border-zinc-800 bg-black py-3 text-white outline-none transition-colors duration-300 focus:border-[var(--brand)]';

function Field({ label, id, children }) {
  return (
    <label htmlFor={id} className="block">
      <span className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">{label}</span>
      {children}
    </label>
  );
}

export default function Contact() {
  const { lang, t } = useSite();
  useSEO(t.meta.contact, 'contact', lang);
  const [searchParams] = useSearchParams();
  const [brand, setBrand] = useState(searchParams.get('marca') === 'souvenir' ? 'souvenir' : 'media');

  useEffect(() => {
    const m = searchParams.get('marca');
    if (m === 'media' || m === 'souvenir') setBrand(m);
  }, [searchParams]);
  const [form, setForm] = useState({});
  const [status, setStatus] = useState('idle');

  const accent = brand === 'media' ? '#14DA64' : '#0CBEDD';
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setStatus('sending');
    try {
      await axios.post(`${API}/leads`, {
        brand,
        name: form.name || '',
        email: form.email || '',
        phone: form.phone || '',
        company: form.company || '',
        service: brand === 'media' ? form.service || '' : '',
        budget: brand === 'media' ? form.budget || '' : '',
        venue_type: brand === 'souvenir' ? form.venue_type || '' : '',
        location: brand === 'souvenir' ? form.location || '' : '',
        message: form.message || '',
        consent: !!form.consent,
        lang,
      });
      setStatus('success');
    } catch {
      setStatus('error');
    }
  };

  return (
    <div style={{ '--brand': accent }} data-testid="contact-page">
      <section className="mx-auto max-w-[1600px] px-5 pb-16 pt-40 sm:px-8 sm:pt-48">
        <Kicker>{t.contact.kicker}</Kicker>
        <h1 className="mt-6 font-heading text-5xl font-black uppercase tracking-tight sm:text-7xl">
          <MaskedLines delay={0.1} lines={[<span key="t" className="brand-text">{t.contact.title}</span>]} />
        </h1>
        <Reveal delay={0.4}>
          <p className="mt-6 max-w-xl text-zinc-400">{t.contact.desc}</p>
          <p className="mt-6 font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">
            {t.contact.generalEmailLabel}
          </p>
          <a
            href="mailto:geral@flgroup.pt"
            data-testid="contact-general-email"
            data-cursor={t.cursor.contact}
            className="mt-2 inline-block font-heading text-2xl font-bold text-white underline decoration-zinc-700 underline-offset-8 transition-colors duration-300 hover:decoration-[var(--brand)] sm:text-3xl"
          >
            geral@flgroup.pt
          </a>
          <p className="mt-6 font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">
            {t.contact.phoneLabel}
          </p>
          <a
            href="tel:+351915562805"
            data-testid="contact-general-phone"
            data-cursor={t.cursor.contact}
            className="mt-2 inline-block font-heading text-2xl font-bold text-white underline decoration-zinc-700 underline-offset-8 transition-colors duration-300 hover:decoration-[var(--brand)] sm:text-3xl"
          >
            +351 915 562 805
          </a>
          <a
            href="https://wa.me/351915562805"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="contact-whatsapp"
            data-cursor={t.cursor.contact}
            className="mt-6 inline-flex items-center gap-2.5 border border-zinc-700 px-6 py-3 font-mono text-[11px] uppercase tracking-[0.25em] text-white transition-all duration-300 hover:border-[var(--brand)] hover:bg-[var(--brand)] hover:text-black"
          >
            <MessageCircle className="h-4 w-4" />
            {t.contact.whatsappCta}
          </a>
        </Reveal>
      </section>

      <section className="mx-auto max-w-[1600px] px-5 pb-24 sm:px-8 sm:pb-32">
        <div className="mb-12 grid gap-px bg-zinc-900 sm:grid-cols-2" role="tablist" data-testid="contact-brand-tabs">
          {[
            { key: 'media', label: t.contact.mediaTab, tag: t.contact.mediaTag, cta: t.contact.mediaCta, color: '#14DA64' },
            { key: 'souvenir', label: t.contact.souvenirTab, tag: t.contact.souvenirTag, cta: t.contact.souvenirCta, color: '#0CBEDD' },
          ].map((b) => (
            <button
              key={b.key}
              role="tab"
              aria-selected={brand === b.key}
              onClick={() => { setBrand(b.key); setStatus('idle'); }}
              data-testid={`contact-tab-${b.key}`}
              className={`group bg-black p-8 text-left transition-colors duration-500 hover:bg-zinc-950 sm:p-10 ${
                brand === b.key ? 'outline outline-1 -outline-offset-1' : ''
              }`}
              style={brand === b.key ? { outlineColor: b.color } : {}}
            >
              <p className="font-heading text-3xl font-black uppercase tracking-tight" style={{ color: b.color }}>
                {b.label}
              </p>
              <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">{b.tag}</p>
              <p className="mt-4 font-mono text-xs uppercase tracking-[0.25em] text-white">{b.cta} →</p>
            </button>
          ))}
        </div>

        <div className="-mt-6 mb-10 flex flex-wrap items-center gap-3" data-testid="contact-brand-email-row">
          <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">
            {brand === 'media' ? 'F&L Media' : 'F&L Souvenir'} · Email
          </span>
          <a
            href={`mailto:${brand === 'media' ? 'media@flgroup.pt' : 'souvenir@flgroup.pt'}`}
            data-testid="contact-brand-email"
            data-cursor={t.cursor.contact}
            className="font-mono text-xs tracking-widest text-zinc-300 underline decoration-zinc-700 underline-offset-4 transition-colors hover:text-white"
          >
            {brand === 'media' ? 'media@flgroup.pt' : 'souvenir@flgroup.pt'}
          </a>
        </div>

        {status === 'success' ? (
          <Reveal className="border border-zinc-800 p-10 text-center sm:p-16">
            <CheckCircle2 className="mx-auto h-10 w-10 brand-text" />
            <h2 className="mt-6 font-heading text-2xl font-extrabold uppercase" data-testid="contact-success">
              {t.contact.form.successTitle}
            </h2>
            <p className="mt-3 text-zinc-400">{t.contact.form.successText}</p>
          </Reveal>
        ) : (
          <Reveal>
            <form onSubmit={submit} className="grid gap-8 sm:grid-cols-2" data-testid={`contact-form-${brand}`}>
              <Field label={t.contact.form.name} id="name">
                <input id="name" required data-testid="field-name" className={inputCls} onChange={set('name')} />
              </Field>
              <Field label={t.contact.form.email} id="email">
                <input id="email" type="email" required data-testid="field-email" className={inputCls} onChange={set('email')} />
              </Field>
              <Field label={t.contact.form.phone} id="phone">
                <input id="phone" type="tel" data-testid="field-phone" className={inputCls} onChange={set('phone')} />
              </Field>
              <Field label={brand === 'media' ? t.contact.form.companyOptional : t.contact.form.venue} id="company">
                <input id="company" data-testid="field-company" className={inputCls} onChange={set('company')} />
              </Field>

              {brand === 'media' && (
                <>
                  <Field label={t.contact.form.service} id="service">
                    <select id="service" data-testid="field-service" className={selectCls} onChange={set('service')} defaultValue="">
                      <option value="" disabled>{t.contact.form.select}</option>
                      {t.contact.form.services.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </Field>
                  <Field label={t.contact.form.budget} id="budget">
                    <select id="budget" data-testid="field-budget" className={selectCls} onChange={set('budget')} defaultValue="">
                      <option value="" disabled>{t.contact.form.select}</option>
                      {t.contact.form.budgets.map((b) => <option key={b} value={b}>{b}</option>)}
                    </select>
                  </Field>
                </>
              )}

              {brand === 'souvenir' && (
                <>
                  <Field label={t.contact.form.venueType} id="venue_type">
                    <select id="venue_type" data-testid="field-venue-type" className={selectCls} onChange={set('venue_type')} defaultValue="">
                      <option value="" disabled>{t.contact.form.select}</option>
                      {t.contact.form.venueTypes.map((v) => <option key={v} value={v}>{v}</option>)}
                    </select>
                  </Field>
                  <Field label={t.contact.form.location} id="location">
                    <input id="location" data-testid="field-location" className={inputCls} onChange={set('location')} />
                  </Field>
                </>
              )}

              <div className="sm:col-span-2">
                <Field label={t.contact.form.message} id="message">
                  <textarea
                    id="message"
                    required
                    rows={5}
                    placeholder={t.contact.form.messagePlaceholder}
                    data-testid="field-message"
                    className={`${inputCls} resize-none border`}
                    style={{ padding: '1rem' }}
                    onChange={set('message')}
                  />
                </Field>
              </div>

              <label className="flex items-start gap-3 sm:col-span-2" data-testid="field-consent-label">
                <input
                  type="checkbox"
                  required
                  data-testid="field-consent"
                  onChange={set('consent')}
                  className="mt-1 h-4 w-4 shrink-0 appearance-none border border-zinc-700 bg-transparent transition-colors checked:border-[var(--brand)] checked:bg-[var(--brand)]"
                />
                <span className="text-sm text-zinc-400">
                  {t.contact.form.consentPrefix}{' '}
                  <Link to={pathFor('privacy', lang)} className="underline underline-offset-4 hover:text-white" data-testid="consent-privacy-link">
                    {t.contact.form.privacyLink}
                  </Link>
                  .
                </span>
              </label>

              {status === 'error' && (
                <p className="text-sm text-red-400 sm:col-span-2" data-testid="contact-error">{t.contact.form.errorText}</p>
              )}

              <div className="sm:col-span-2">
                <button
                  type="submit"
                  disabled={status === 'sending'}
                  data-testid="contact-submit"
                  data-cursor={t.cursor.contact}
                  className="group inline-flex items-center gap-3 border px-10 py-4 font-mono text-xs uppercase tracking-[0.25em] transition-colors duration-300 brand-border hover:!bg-[var(--brand)] hover:text-black disabled:opacity-50"
                >
                  {status === 'sending' ? t.contact.form.sending : t.contact.form.submit}
                  <Send className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                </button>
              </div>
            </form>
          </Reveal>
        )}
      </section>
    </div>
  );
}
